import {Injectable} from "@angular/core";
import {SubscriptionType} from "../enums/subscription-type";
import {SubscriptionPlanInfoResponse} from "../interface/subscriptionPlanInfoResponse";
import {
  UNLIMITED_CONTENT_LINKS_SUBSCRIPTION_TYPES,
  UNLIMITED_PAYMENT_METHODS_SUBSCRIPTION_TYPES
} from "../constants/subscriptionInfo.constants";
import {UserCardContentRequest} from "../interface/userCardContentRequest";
import {ContentType} from "../interface/contentType";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class UserCardSubscriptionUtil {

  constructor(
    private snackBarUtil: SnackBarUtil,
    private readonly translate: TranslateService
  ) {
  }

  checkSubscriptionPermittedContentLimits(userCardContent: UserCardContentRequest, subscriptionInfo: SubscriptionPlanInfoResponse, userCardContentRequest: UserCardContentRequest []) {
    const activeUserCardContents = userCardContentRequest.filter(content => content.active && content.type !== ContentType.DOCUMENT).length;
    const activeUserCardPaymentContents = userCardContentRequest.filter(content => content.active && content.type === ContentType.PAYMENT).length;

    const subscriptionContentLinkCounts = this.getSubscriptionContentLimitCountBySubscription(subscriptionInfo);
    const subscriptionPaymentsMethodCount = this.getSubscriptionPaymentMethodCountBySubscription(subscriptionInfo);
    const dynamicMethodTitle = this.getSubscriptionDynamicMethodTitle(subscriptionPaymentsMethodCount);

    if (this.isContentLimitExceeded(userCardContent, activeUserCardContents, subscriptionContentLinkCounts, subscriptionInfo)) {
      this.disableContentWithError(userCardContent, subscriptionInfo, subscriptionPaymentsMethodCount, dynamicMethodTitle);

    } else if (this.isPaymentLimitExceeded(userCardContent, activeUserCardPaymentContents, subscriptionPaymentsMethodCount)) {
      this.disableContentWithError(userCardContent, subscriptionInfo, subscriptionPaymentsMethodCount, dynamicMethodTitle);

    } else if (this.isUnlimitedOrPremiumSubscription(subscriptionInfo, subscriptionPaymentsMethodCount)) {
      userCardContent.active = true;
    }
  }

  checkIsContentShouldBeDisabled(userCardContent: UserCardContentRequest, subscriptionInfo: SubscriptionPlanInfoResponse, userCardContentRequest: UserCardContentRequest []) {
    const activeUserCardContents = userCardContentRequest.filter(content => content.active && content.type !== ContentType.DOCUMENT).length;
    const activeUserCardPaymentContents = userCardContentRequest.filter(content => content.active && content.type === ContentType.PAYMENT).length;

    const subscriptionContentLinkCounts = this.getSubscriptionContentLimitCountBySubscription(subscriptionInfo);
    const subscriptionPaymentsMethodCount = this.getSubscriptionPaymentMethodCountBySubscription(subscriptionInfo);

    if (subscriptionContentLinkCounts !== null) {

      if (userCardContent.type === ContentType.PAYMENT) {
        return this.shouldDisablePaymentContent(userCardContent, activeUserCardPaymentContents, subscriptionPaymentsMethodCount);
      }

      return this.shouldDisableLinkContent(userCardContent, activeUserCardContents, subscriptionInfo);
    }

    return !this.isUnlimitedOrPremiumSubscription(subscriptionInfo, subscriptionPaymentsMethodCount);
  }

  private showErrorSnackbarForContentLimits(
    subscriptionInfo: SubscriptionPlanInfoResponse,
    subscriptionPaymentsMethodCount: number,
  ) {
    const message = `${this.translate.instant('messages.plane_limit')} ${subscriptionInfo?.linkCount} ${this.translate.instant('messages.link_count')} ${subscriptionPaymentsMethodCount} ${this.translate.instant('messages.payment')}.`;

    this.showSnackbar(MockupDataModel.info(message), SnackBarUtil.CLASSES.info);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  private disableContentWithError(userCardContent: UserCardContentRequest, subscriptionInfo: SubscriptionPlanInfoResponse, subscriptionPaymentsMethodCount: number, dynamicMethodTitle: string) {
    this.showErrorSnackbarForContentLimits(subscriptionInfo, subscriptionPaymentsMethodCount);
    userCardContent.active = false;
  }

  private getSubscriptionContentLimitCountBySubscription(subscriptionInfo: SubscriptionPlanInfoResponse) {
    return subscriptionInfo?.linkCount === null && !UNLIMITED_CONTENT_LINKS_SUBSCRIPTION_TYPES.includes(subscriptionInfo.subscriptionType)
      ? 0
      : subscriptionInfo?.linkCount;
  }

  private getSubscriptionPaymentMethodCountBySubscription(subscriptionInfo: SubscriptionPlanInfoResponse) {
    return subscriptionInfo?.paymentMethodCount === null && !UNLIMITED_PAYMENT_METHODS_SUBSCRIPTION_TYPES.includes(subscriptionInfo.subscriptionType)
      ? 0
      : subscriptionInfo?.paymentMethodCount;
  }

  private getSubscriptionDynamicMethodTitle(subscriptionPaymentsMethodCount: number) {
    return subscriptionPaymentsMethodCount > 0
      ? 'methods'
      : 'method';
  }

  private isContentLimitExceeded(userCardContent: UserCardContentRequest, activeUserCardContents: number, subscriptionContentLinkCounts: number, subscriptionInfo: SubscriptionPlanInfoResponse): boolean {
    return activeUserCardContents >= subscriptionContentLinkCounts &&
      userCardContent.type !== ContentType.DOCUMENT &&
      subscriptionInfo?.linkCount !== null;
  }

  private isPaymentLimitExceeded(userCardContent: UserCardContentRequest, activeUserCardPaymentContents: number, subscriptionPaymentsMethodCount: number): boolean {
    return userCardContent.type === ContentType.PAYMENT &&
      activeUserCardPaymentContents >= subscriptionPaymentsMethodCount &&
      subscriptionPaymentsMethodCount !== null;
  }

  private shouldDisablePaymentContent(userCardContent: UserCardContentRequest, activeUserCardPaymentContents: number, subscriptionPaymentsMethodCount: number): boolean {
    return !userCardContent.active && activeUserCardPaymentContents >= subscriptionPaymentsMethodCount;
  }

  private shouldDisableLinkContent(userCardContent: UserCardContentRequest, activeUserCardContents: number, subscriptionInfo: SubscriptionPlanInfoResponse): boolean {
    return !userCardContent.active && activeUserCardContents >= subscriptionInfo?.linkCount && userCardContent.type !== ContentType.DOCUMENT;
  }

  private isUnlimitedOrPremiumSubscription(subscriptionInfo: SubscriptionPlanInfoResponse, subscriptionPaymentsMethodCount: number): boolean {
    return subscriptionInfo?.linkCount === null || (subscriptionPaymentsMethodCount !== null && subscriptionInfo?.subscriptionType === SubscriptionType.PREMIUM);
  }
}
